import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AlertConfirmationData,
  AlertConfirmationStatus,
  TeeTimeActionPayload,
} from '../../../model/chat.model';
import { SharedService } from '../../../shared/services/shared.service';

@Component({
  selector: 'app-alert-confirmation',
  imports: [CurrencyPipe, DatePipe],
  templateUrl: './alert-confirmation.component.html',
  styleUrl: './alert-confirmation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertConfirmationComponent {
  private readonly _shared = inject(SharedService);

  readonly data = input.required<AlertConfirmationData>();
  readonly embedded = input<boolean>(false);
  /** When false (stale chat message) the cancel link is hidden. */
  readonly interactive = input<boolean>(true);
  readonly action = output<TeeTimeActionPayload>();

  protected readonly submitted = signal(false);
  protected readonly currentUser = toSignal(this._shared.user$, { initialValue: undefined });

  protected readonly status = computed<AlertConfirmationStatus | undefined>(() => this.data().status);
  protected readonly criteria = computed(() => this.data().criteria);
  protected readonly course = computed(() => this.data().course);
  protected readonly customer = computed(() => this.data().user);
  protected readonly costSummary = computed(() => this.data().costSummary);

  protected readonly currency = computed(() => this.costSummary()?.currency ?? 'USD');

  protected readonly paymentStatusLabel = computed(() => {
    switch (this.costSummary()?.paymentStatus) {
      case 'paid': return 'Paid';
      case 'refunded': return 'Refunded';
      case 'not_applicable': return '';
      case 'pending':
      default: return 'Pending';
    }
  });

  protected readonly locationDisplay = computed(() => this.course().location ?? '');

  /** Unicode regional-indicator flag from an ISO 3166-1 alpha-2 country code (e.g. "US" → "🇺🇸"). */
  protected readonly countryFlag = computed(() => {
    const code = (this.course().countryCode ?? '').trim().toUpperCase();
    if (code.length !== 2) return '';
    const base = 0x1f1e6;
    const a = 'A'.charCodeAt(0);
    return String.fromCodePoint(base + (code.charCodeAt(0) - a), base + (code.charCodeAt(1) - a));
  });

  /** Status drives the header background hue — active is the brand blue, terminal states fade out. */
  protected readonly headerVariant = computed<'active' | 'notified' | 'neutral' | 'error'>(() => {
    switch (this.status()) {
      case 'notified': return 'notified';
      case 'expired':
      case 'cancelled': return 'neutral';
      case 'notify_failed': return 'error';
      case 'active':
      case 'processing':
      default: return 'active';
    }
  });

  protected readonly canCancel = computed(() => {
    if (!this.interactive()) return false;
    const s = this.status();
    if (s !== 'active' && s !== 'processing') return false;
    return !!(this.data().selection?.alertId ?? this.data().alertId);
  });

  cancelAlert(): void {
    if (this.submitted() || !this.canCancel()) return;
    const alertId = this.data().selection?.alertId ?? this.data().alertId;
    if (!alertId) return;
    this.submitted.set(true);
    this.action.emit({
      question: 'Cancel alert',
      action: {
        type: 'cancel_alert',
        alertId,
      },
    });
  }
}
